// InvitePublic — public landing for `/invite/<token>`.
//
// Flow:
//   1. Look up the token. If invalid → friendly error.
//   2. Show a soft welcome card with the campaign name + a "Commencer" CTA.
//   3. On Commencer: mark the participant as "started" server-side, then
//      mount DiagnosticPage. Its `onComplete` saves through the token-based
//      endpoint (no X-User-ID).
//   4. After the DiagnosticDoc renders, the participant can copy / print.
//
// No auth, no app shell — the DiagIA sidebar would be confusing for someone
// who isn't an admin. We render a minimal frame.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaSpinner, FaCheckCircle } from 'react-icons/fa';

import DiagnosticPage from '../../diagnostic/DiagnosticPage';
import useDiagiaApi from '../hooks/useDiagiaApi';

const PHASE = {
  LOADING: 'loading',
  WELCOME: 'welcome',
  ALREADY_DONE: 'already',
  INTAKE: 'intake',
  NOT_FOUND: 'not_found',
  ERROR: 'error',
};

export default function InvitePublic() {
  const { t } = useTranslation();
  const { token } = useParams();
  const api = useDiagiaApi();

  const [phase, setPhase] = useState(PHASE.LOADING);
  const [errorMsg, setErrorMsg] = useState('');
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.lookupInvite(token)
      .then((data) => {
        if (cancelled) return;
        setInvite(data);
        if (data?.participant?.status === 'completed') {
          setPhase(PHASE.ALREADY_DONE);
        } else {
          setPhase(PHASE.WELCOME);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setPhase(PHASE.NOT_FOUND);
        } else {
          setErrorMsg(err?.message || 'Erreur');
          setPhase(PHASE.ERROR);
        }
      });
    return () => { cancelled = true; };
  }, [api, token]);

  const start = async () => {
    try { await api.startInvite(token); } catch (e) { /* non-fatal */ }
    setPhase(PHASE.INTAKE);
  };

  const handleComplete = async ({ markdown, transcript, language }) => {
    try {
      await api.saveInviteDiagnostic(token, { markdown, transcript, language });
    } catch (e) {
      console.warn('invite save failed', e);
    }
  };

  if (phase === PHASE.LOADING) {
    return (
      <div className="dia-invite-frame">
        <div className="dia-invite-card">
          <FaSpinner className="dia-spin" /> Chargement de votre invitation…
        </div>
      </div>
    );
  }

  if (phase === PHASE.NOT_FOUND) {
    return (
      <div className="dia-invite-frame">
        <div className="dia-invite-card">
          <h2>{t('diagia.invite.not_found', 'Invitation introuvable')}</h2>
          <p>{t('diagia.invite.not_found_body', "Le lien que vous avez ouvert n'est plus valide ou a déjà été révoqué.")}</p>
          <p className="dia-table-muted">
            {t('diagia.invite.not_found_hint', "Demandez à l'administrateur de la campagne de vous renvoyer une invitation.")}
          </p>
        </div>
      </div>
    );
  }

  if (phase === PHASE.ERROR) {
    return (
      <div className="dia-invite-frame">
        <div className="dia-invite-card">
          <h2>{t('diagia.invite.error', 'Erreur')}</h2>
          <p>{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === PHASE.ALREADY_DONE) {
    return (
      <div className="dia-invite-frame">
        <div className="dia-invite-card">
          <FaCheckCircle size={28} className="dia-invite-check" />
          <h2>{t('diagia.invite.already_done', 'Diagnostic déjà complété')}</h2>
          <p>
            {t('diagia.invite.already_done_body', 'Vous avez déjà soumis votre diagnostic pour la campagne')}&nbsp;
            <strong>{invite?.campaign?.name}</strong>. {t('diagia.invite.thanks', 'Merci !')}
          </p>
        </div>
      </div>
    );
  }

  if (phase === PHASE.WELCOME) {
    return (
      <div className="dia-invite-frame">
        <div className="dia-invite-card">
          <span className="dia-section-eyebrow">{t('diagia.invite.eyebrow', 'Invitation')}</span>
          <h1 className="dia-invite-title">{invite?.campaign?.name}</h1>
          {invite?.campaign?.scope && (
            <p className="dia-invite-scope">{invite.campaign.scope}</p>
          )}
          {invite?.campaign?.description && (
            <p className="dia-invite-desc">{invite.campaign.description}</p>
          )}

          <ul className="dia-invite-bullets">
            <li>{t('diagia.invite.bullet1', '5 questions ouvertes, 3 minutes au total.')}</li>
            <li>{t('diagia.invite.bullet2', 'Une IA cadre votre réponse en fiche structurée.')}</li>
            <li>{t('diagia.invite.bullet3', "Vos réponses sont partagées avec l'administrateur de la campagne.")}</li>
          </ul>

          <button type="button" className="dia-cta dia-cta-primary" onClick={start}>
            {t('diagia.invite.start', 'Commencer mon diagnostic')} <FaArrowRight />
          </button>
          <p className="dia-invite-footer">
            {t('diagia.invite.hello', 'Bonjour')} {invite?.participant?.email}.
          </p>
        </div>
      </div>
    );
  }

  // INTAKE — reuse the existing diagnostic UI.
  return (
    <DiagnosticPage onComplete={handleComplete} embedded inviteToken={token} onExit={() => setPhase(PHASE.WELCOME)} />
  );
}
